import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import MeasurementSummaryCard from './MeasurementSummaryCard';
import { measurementLocalDayUtcRange } from '../../utils/measurementTime';

type LatestMeasurement = {
    id: number;
    takenAt: string;
    measuredTimezone?: string | null;
    measuredUtcOffsetMinutes?: number | null;
    timeSource?: string | null;
    timezoneSource?: string | null;
    lat: number | null;
    lng: number | null;
    areaId: number | null;
    areaName: string | null;
    areaPolygon: any;
    areaType?: { id: number; name: string } | null;
    measurementCode: string;
    metrics: Record<string, number | null>;
    trends?: Record<string, { direction: 'up' | 'down' | 'same' | 'none'; previous?: number | null }>;
};

type MetricDefinition = {
    key: string;
    name: string;
    unit?: string | null;
    sourceKey?: string | null;
    valueTypeKey?: string | null;
    positiveIsGood?: boolean;
};

type DashboardOverview = {
    latestMeasurement: LatestMeasurement | null;
    latestDayMeasurements?: Array<{
        id: number;
        lat: number | null;
        lng: number | null;
        measurementCode: string | null;
    }>;
    overall: {
        totalMeasurements: number;
        measurementsLast7d: number;
        areas: number;
        devices: number;
    };
};

interface LatestMeasurementCardProps {
    companyId: number;
    preferredMetricKeys?: string[];
    onMeasurementClick?: (measurementId: string | number, areaId?: number | null, rangeOverride?: [Dayjs, Dayjs]) => void;
    onAreaClick?: (areaId: number, rangeOverride?: [Dayjs, Dayjs]) => void;
}

/**
 * Self-contained component for displaying the latest measurement
 * Handles its own data fetching, template loading, and metric calculation
 */
const LatestMeasurementCard: React.FC<LatestMeasurementCardProps> = ({
    companyId,
    preferredMetricKeys = [],
    onMeasurementClick,
    onAreaClick,
}) => {
    const [overview, setOverview] = useState<DashboardOverview | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [templateImageUrl, setTemplateImageUrl] = useState<string | null>(null);
    const [metricDefinitions, setMetricDefinitions] = useState<MetricDefinition[]>([]);
    const [emptyDescription, setEmptyDescription] = useState('No recent measurements were found.');

    // Fetch overview data
    useEffect(() => {
        const load = async () => {
            if (!companyId) {
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const response = await axios.get(ENDPOINTS.DASHBOARD_OVERVIEW(companyId), { withCredentials: true });
                const payload = response.data?.data || response.data;
                setOverview(payload);
                setEmptyDescription('No recent measurements were found.');
            } catch (error) {
                console.warn('Latest measurement fetch failed', error);
                setOverview(null);
                const status = axios.isAxiosError(error) ? error.response?.status : undefined;
                setEmptyDescription(
                    status === 401 || status === 403 || status === undefined
                        ? 'Latest measurement data is currently unavailable.'
                        : 'No measurements were found in the dashboard date range.'
                );
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [companyId]);

    // Fetch metric definitions
    useEffect(() => {
        const loadMetricDefinitions = async () => {
            try {
                const response = await axios.get(`${ENDPOINTS.METRICS}?active=true`, { withCredentials: true });
                const metrics = response.data?.data || [];
                setMetricDefinitions(
                    metrics
                        .filter((metric: any) => typeof metric?.key === 'string')
                        .map((metric: any) => ({
                            key: metric.key,
                            name: metric.name || metric.key,
                            unit: metric.unit || null,
                            sourceKey: metric.sourceKey || null,
                            valueTypeKey: metric.valueTypeKey || null,
                            positiveIsGood: metric.positiveIsGood ?? true,
                        }))
                );
            } catch (error) {
                console.warn('Metric definitions fetch failed', error);
            }
        };
        loadMetricDefinitions();
    }, []);

    // Load template image for the area type
    useEffect(() => {
        const latest = overview?.latestMeasurement;
        if (!latest?.areaType?.id) {
            setTemplateImageUrl(null);
            return;
        }

        (async () => {
            try {
                const areaTypeId = latest.areaType?.id;
                if (!areaTypeId) {
                    setTemplateImageUrl(null);
                    return;
                }

                const typeRes = await axios.get(`${ENDPOINTS.AREA_TYPES_V2}/${areaTypeId}`, {
                    withCredentials: true,
                });
                const hasTemplate = typeRes.data?.data?.templateImage || typeRes.data?.templateImage;

                if (hasTemplate) {
                    const imageUrl = `${ENDPOINTS.AREA_TYPES_V2}/${areaTypeId}/template-image?t=${Date.now()}`;
                    setTemplateImageUrl(imageUrl);
                } else {
                    setTemplateImageUrl(null);
                }
            } catch (err) {
                console.error('[LatestMeasurementCard] Failed to check template image:', err);
                setTemplateImageUrl(null);
            }
        })();
    }, [overview?.latestMeasurement?.areaType?.id]);

    const latest = overview?.latestMeasurement || null;

    const latestDateRange = useMemo(() => {
        if (!latest?.takenAt) return null;
        const range = measurementLocalDayUtcRange(latest.takenAt, latest);
        return range ? [dayjs(range[0]), dayjs(range[1])] as [Dayjs, Dayjs] : null;
    }, [latest]);

    const metricDefinitionByKey = useMemo(() => {
        const map = new Map<string, MetricDefinition>();
        metricDefinitions.forEach((metric) => {
            if (metric.key) {
                map.set(metric.key.toLowerCase(), metric);
            }
        });
        return map;
    }, [metricDefinitions]);

    const latestMetrics = useMemo(() => {
        if (!latest?.metrics) return [];
        const entries = Object.entries(latest.metrics);
        const entryByKey = new Map(entries.map(([key, value]) => [key, value]));
        const availableKeyMap = new Map(entries.map(([key]) => [key.toLowerCase(), key]));

        const findDefinitionForSelection = (selectedKey: string) => {
            const selectedLower = selectedKey.toLowerCase();
            const direct = metricDefinitionByKey.get(selectedLower);
            if (direct) return direct;
            for (const def of metricDefinitions) {
                const aliases = [def.key, def.valueTypeKey, def.sourceKey].filter(Boolean) as string[];
                if (aliases.some((alias) => alias.toLowerCase() === selectedLower)) {
                    return def;
                }
            }
            return undefined;
        };

        const resolveMetricKey = (selectedKey: string, definition?: MetricDefinition) => {
            const candidates = new Set<string>();
            if (definition?.valueTypeKey) candidates.add(definition.valueTypeKey);
            if (definition?.sourceKey) candidates.add(definition.sourceKey);
            if (definition?.key) candidates.add(definition.key);
            candidates.add(selectedKey);
            for (const candidate of candidates) {
                const resolved = availableKeyMap.get(candidate.toLowerCase());
                if (resolved) return resolved;
            }
            return null;
        };

        const formatEntry = (
            metricKey: string,
            label: string,
            unit: string | null | undefined,
            value: number | null | undefined,
            trend: 'up' | 'down' | 'same' | 'none',
            positiveIsGood: boolean
        ) => {
            const displayValue = typeof value === 'number' ? value.toFixed(2) : '-';
            return {
                key: metricKey,
                label,
                value: unit && displayValue !== '-' ? `${displayValue} ${unit}` : displayValue,
                trend,
                positiveIsGood,
            };
        };

        if (preferredMetricKeys.length === 0) {
            return entries.map(([key, value]) => {
                const definition = metricDefinitionByKey.get(key.toLowerCase());
                const label = definition?.name || key.replace(/_/g, ' ');
                const unit = definition?.unit;
                const trendRecord = latest?.trends?.[key.toLowerCase()];
                const displayTrend = trendRecord ? trendRecord.direction : 'none';
                const positiveIsGood = definition?.positiveIsGood ?? true;
                return formatEntry(
                    key,
                    label,
                    unit,
                    value,
                    displayTrend,
                    positiveIsGood
                );
            });
        }

        return preferredMetricKeys.map((selectedKey) => {
            const definition = findDefinitionForSelection(selectedKey);
            const resolvedKey = resolveMetricKey(selectedKey, definition);
            const lookupKey = resolvedKey || selectedKey;
            const value = resolvedKey ? entryByKey.get(resolvedKey) : undefined;
            const trendRecord =
                resolvedKey && latest?.trends?.[resolvedKey.toLowerCase()];
            const displayTrend = trendRecord ? trendRecord.direction : 'none';
            const label = definition?.name || selectedKey.replace(/_/g, ' ');
            const unit = definition?.unit;
            const positiveIsGood = definition?.positiveIsGood ?? true;
            return formatEntry(lookupKey, label, unit, value, displayTrend, positiveIsGood);
        });
    }, [latest, preferredMetricKeys, metricDefinitionByKey, metricDefinitions]);

    if (loading) {
        return null; // Or return a skeleton/spinner if desired
    }

    return (
        <MeasurementSummaryCard
            title="Latest measurement"
            measurement={latest}
            templateImageUrl={templateImageUrl}
            metricsToShow={latestMetrics}
            onMeasurementClick={onMeasurementClick}
            onAreaClick={onAreaClick}
            showReportCta
            reportDateRange={latestDateRange}
            areaId={latest?.areaId ?? null}
            dayMeasurements={overview?.latestDayMeasurements || []}
            emptyDescription={emptyDescription}
        />
    );
};

export default LatestMeasurementCard;
